import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import Card from '../../../../../ui/canon/Card';
import SuccessionLayout from '../SuccessionLayout';
import DealMenu from './DealMenu';
import ArrowTabs from './ArrowTabs';
import GuidedTour from '../../Shared/GuidedTour/GuidedTour';
import { formatCurrency, currencySymbol } from '../../../utils/formatters';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { get, split } from 'lodash';
import html2pdf from 'html2pdf.js';
import MatchPreferencesModal from './MatchPreferencesModal';

// Additional queries for company settings and user data
const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      name
      agent_word
      agent_word_fr
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      stylesheet_name
      business_type
      allow_blocking_in_messages
      teaser_hide_business_overview_question
      hide_list_price_in_deal_room_wizard_succession
      hide_list_price_in_deal_room_wizard_sale
      hide_list_price_in_deal_room_wizard_continuity
      avatar_background_color
      avatar_directory
      primary_color
    }
    getCurrentUser {
      id
      motivation
      locale
      name
      email
      company {
        id
        name
        host
      }
    }
  }
`;

const GET_DEAL_INTEREST = gql`
  query GetDealInterest {
    getCurrentUser {
      id
      inventory_user {
        id
        deal_interests {
          id
          workflow_state
          invited
          receiving_party_nda_sent_timestamp
          disclosing_party_nda_sent_timestamp
          a1
          a2
          a3
          a4
          a5
          proposal_letter
          proposal_meeting_availability
          file_url
          documents {
            id
            name
            download_url
            created_at
          }
          deal {
            id
            deal_name
          }
          inventory_user {
            id
            user {
              id
              allowed_name
              city
              province
              motivation
              user_name
              headline
              assets_under_management
              annual_revenue
              fb_user_name
            }
          }
        }
      }
    }
    company {
      id
      name
    }
  }
`;

const DEAL_TEASER_TOUR_STORAGE_KEY_PREFIX = 'deal_teaser_tour_shown_v1_';

// Error boundary component to catch Apollo errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console with detailed info
    console.error("GraphQL Error caught:", error);
    console.error("Component stack:", errorInfo?.componentStack);
    
    // Update state to include error details
    this.setState({ errorInfo });
    
    // You could also log to an error reporting service here
    // errorReportingService.logError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI or use the provided fallback prop
      return this.props.fallback || (
        <div className="p-8 bg-white rounded-md shadow">
          <h2 className="text-xl font-bold mb-4">
            <FormattedMessage id="succession_deals.deal_teaser.error_boundary.something_went_wrong" />
          </h2>
          <p className="mb-4">
            <FormattedMessage id="succession_deals.deal_teaser.error_boundary.error_occurred" />
          </p>
          {process.env.NODE_ENV !== 'production' && (
            <div className="mt-4 p-4 bg-gray-100 rounded overflow-auto text-xs">
              <p className="font-bold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.error_boundary.error_label" />
              </p>
              <p className="mb-4">{this.state.error?.toString()}</p>
              {this.state.errorInfo && (
                <>
                  <p className="font-bold mb-2">
                    <FormattedMessage id="succession_deals.deal_teaser.error_boundary.component_stack_label" />
                  </p>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </>
              )}
            </div>
          )}
          <div className="mt-4">
            <Link to="/succession/deals" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover">
              <FormattedMessage id="succession_deals.deal_teaser.error_boundary.return_to_deals" />
            </Link>
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

const DealTeaser = ({ deal, refetchDeal, isDealOwner, dealInterestId, currentUser, userMotivation }) => {
  const apolloClient = useApolloClient();
  const intl = useIntl();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [justSigned, setJustSigned] = useState(false);
  const [settings, setSettings] = useState({ company: {}, getCurrentUser: {} });
  const [interestInfo, setInterestInfo] = useState(null);

  // State for match preferences modal
  const [showMatchPreferencesModal, setShowMatchPreferencesModal] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // Enhanced scroll animation state
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Enhanced Intersection Observer for Apple-style scroll animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-5% 0px -15% 0px', // Trigger earlier for smoother transitions
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] // Multiple thresholds for progressive animations
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const element = entry.target;
        const scrollId = element.dataset.scrollId;
        
        if (entry.isIntersecting) {
          // Add to visible elements immediately
          setVisibleElements(prev => new Set([...prev, scrollId]));
          
          // Add classes for progressive reveal based on intersection ratio
          requestAnimationFrame(() => {
            element.style.transition = 'all 1.2s cubic-bezier(0.16, 1, 0.3, 1)';
            
            if (entry.intersectionRatio > 0.1) {
              element.style.opacity = Math.min(1, entry.intersectionRatio * 2);
              element.style.transform = `translateY(${Math.max(0, (1 - entry.intersectionRatio) * 60)}px) scale(${0.95 + (entry.intersectionRatio * 0.05)})`;
            }
            
            if (entry.intersectionRatio > 0.3) {
              element.classList.add('scroll-active');
              element.style.transform = `translateY(${Math.max(0, (1 - entry.intersectionRatio) * 30)}px) scale(${0.98 + (entry.intersectionRatio * 0.02)})`;
            }
            
            if (entry.intersectionRatio > 0.6) {
              element.classList.add('scroll-complete');
              element.style.opacity = '1';
              element.style.transform = 'translateY(0px) scale(1)';
            }
          });
        } else if (entry.boundingClientRect.top > window.innerHeight) {
          // Reset elements that are far below viewport (for scroll up scenarios)
          element.style.opacity = '0';
          element.style.transform = 'translateY(60px) scale(0.95)';
        }
      });
    }, observerOptions);
    
    // Initialize and observe all scroll-reveal elements
    setTimeout(() => {
      const scrollElements = document.querySelectorAll('[data-scroll-id]');
      scrollElements.forEach((el, index) => {
        // Initialize elements as hidden with staggered delays
        el.style.opacity = '0';
        el.style.transform = 'translateY(60px) scale(0.95)';
        el.style.transition = `all 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.1}s`;
        
        observer.observe(el);
      });
    }, 100);
    
    return () => observer.disconnect();
  }, []);
  
  // Smooth parallax scroll tracking
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          setScrollDirection(currentScrollY > lastScrollY ? 'down' : 'up');
          setScrollY(currentScrollY);
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Helper function to check if element is visible
  const isVisible = (elementId) => visibleElements.has(elementId);
  
  // Parallax transform calculator for subtle background movement
  const getParallaxTransform = (speed = 0.3, direction = 'y') => {
    const transform = scrollY * speed;
    return direction === 'y' ? `translateY(${transform}px)` : `translateX(${transform}px)`;
  };
  
  // Staggered animation delay calculator
  const getStaggerDelay = (index, baseDelay = 100) => {
    return `${baseDelay + (index * 150)}ms`;
  };
  
  // PDF Generation via server endpoint
  const generatePDF = () => {
    const dealSlug = deal?.deal_name || deal?.id;
    if (!dealSlug) {
      console.error('No deal identifier available for PDF generation');
      return;
    }
    
    // Create download link and trigger it
    const downloadUrl = `/succession/deals/${dealSlug}/teaser.pdf`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${deal?.deal_name || 'deal-teaser'}.pdf`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Check if deal is valid
  if (!deal) {
    console.error("Deal object is undefined or null");
    return (
      <div className="p-8 bg-white rounded-md shadow">
        <h2 className="text-xl font-bold mb-4">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.deal_not_available" />
        </h2>
        <p>
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.unable_to_load" />
        </p>
        <Link to="/succession/deals" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.return_to_deals" />
        </Link>
      </div>
    );
  }
  
  // Safe useQuery with error handling
  const { loading: settingsLoading } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      console.log("Company settings loaded:", data);
      setSettings(data || { company: {}, getCurrentUser: {} });
    },
    onError: (error) => console.error("Settings query error:", error)
  });
  
  // Only query for deal interest if we have a deal ID and user is not the owner
  const { loading: interestLoading } = useQuery(GET_DEAL_INTEREST, {
    skip: !deal.deal_name || isDealOwner,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      console.log("Deal interest loaded:", data);
      setInterestInfo(data);
    },
    onError: (error) => console.error("Interest query error:", error)
  });
  
  // Parse URL search params to check for just_signed parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('just_signed') === 'true') {
      setJustSigned(true);
    }
  }, []);

  useEffect(() => {
    if (!isDealOwner || !currentUser?.id) return;

    const storageKey = `${DEAL_TEASER_TOUR_STORAGE_KEY_PREFIX}${currentUser.id}`;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('tour') === 'reset') {
        localStorage.removeItem(storageKey);
        setTourOpen(true);
        return;
      }
      if (params.get('tour') === 'open') {
        setTourOpen(true);
        return;
      }

      const alreadyShown = localStorage.getItem(storageKey);
      if (!alreadyShown) {
        const timer = setTimeout(() => setTourOpen(true), 500);
        return () => clearTimeout(timer);
      }
    } catch (_) {}
  }, [isDealOwner, currentUser?.id]);

  const handleTourClose = () => {
    setTourOpen(false);
    if (!currentUser?.id) return;
    try {
      localStorage.setItem(`${DEAL_TEASER_TOUR_STORAGE_KEY_PREFIX}${currentUser.id}`, '1');
    } catch (_) {}
  };

  const tourSteps = isDealOwner ? [
    {
      id: 'teaser-overview',
      title: intl.formatMessage({ id: 'dealTeaser.tour.welcome.title' }),
      description: intl.formatMessage({ id: 'dealTeaser.tour.welcome.description' }),
      targetSelector: '[data-tutorial-id="deal-teaser-tab-teaser"]',
      position: 'top',
    },
    {
      id: 'matches-and-invites',
      title: intl.formatMessage({ id: 'dealTeaser.tour.matches_and_invites.title' }),
      description: intl.formatMessage({ id: 'dealTeaser.tour.matches_and_invites.description' }),
      targetSelector: '[data-tutorial-id="deal-teaser-tab-matches"], [data-tutorial-id="deal-teaser-tab-invited"]',
      position: 'bottom',
    },
    {
      id: 'review-and-shortlist',
      title: intl.formatMessage({ id: 'dealTeaser.tour.review_and_shortlist.title' }),
      description: intl.formatMessage({ id: 'dealTeaser.tour.review_and_shortlist.description' }),
      targetSelector: '[data-tutorial-id="deal-teaser-tab-reviewing"], [data-tutorial-id="deal-teaser-tab-shortlisted"]',
      position: 'top',
    },
    {
      id: 'nda-and-negotiation',
      title: intl.formatMessage({ id: 'dealTeaser.tour.nda_and_negotiation.title' }),
      description: intl.formatMessage({ id: 'dealTeaser.tour.nda_and_negotiation.description' }),
      targetSelector: '[data-tutorial-id="deal-teaser-tab-ndas"], [data-tutorial-id="deal-teaser-tab-negotiate"]',
      position: 'bottom',
    },
  ] : [];
  
  const companySettings = settings?.company || {};
  const settingsUser = settings?.getCurrentUser || {};
  const locale = settingsUser.locale || currentUser?.locale || 'en';
  const agentWord = locale === 'fr' ? (companySettings?.agent_word_fr || 'conseiller') : (companySettings?.agent_word || 'Advisor');
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLower = agentWordPlural.toLowerCase();
  
  // Use passed dealInterestId or get it from the query
  // Filter deal interests to find the one matching this deal
  const dealInterest = isDealOwner 
    ? null 
    : (interestInfo?.getCurrentUser?.inventory_user?.deal_interests?.find(di => di.deal?.deal_name === deal?.deal_name) || null);
  
  const documents = dealInterest?.documents || [];
  
  // Determine if user is a buyer or seller
  const isBuyer = userMotivation?.includes('buyer') || userMotivation?.includes('growth');
  const isSeller = userMotivation?.includes('seller') || userMotivation?.includes('succession');
  
  // Feature flags - access directly from company features
  const cfaInDealRoomsEnabled = companySettings?.feature?.includes('cfa_in_deal_rooms');
  const lenderInquiryCTAsEnabled = companySettings?.feature?.includes('lender_inquiry_ctas');
  const lenderOfferCTAsEnabled = companySettings?.feature?.includes('lender_offer_ctas');

  const hideListPrice =
    (deal?.deal_type === 'continuity' && companySettings?.hide_list_price_in_deal_room_wizard_continuity) ||
    (deal?.deal_type === 'succession' && companySettings?.hide_list_price_in_deal_room_wizard_succession) ||
    (deal?.deal_type === 'sale' && companySettings?.hide_list_price_in_deal_room_wizard_sale);
  
  // Since we don't have direct access to lenders, we'll need to handle differently
  const randomLender = null; // No lenders available in the schema
  
  // Simplify eligibility check since we don't have lenders
  const [eligibleOffers, ineligibleOffers] = React.useMemo(() => {
    return [[], []];
  }, [lenderOfferCTAsEnabled, dealInterest, isBuyer, currentUser]);
  
  // Get data from the deal or user profile
  const getData = (field) => {
    // First check the deal object directly
    if (deal?.[field] !== undefined && deal?.[field] !== null) {
      return deal[field];
    }
    // Then check the user profile
    if (deal?.inventory_user?.user?.[field] !== undefined && deal?.inventory_user?.user?.[field] !== null) {
      return deal.inventory_user.user[field];
    }
    return null;
  };
  
  // Get the background image for each slide type
  const getSlideBackground = (slideId) => {
    // Default background
    const defaultBackground = 'https://findbob-production.s3.amazonaws.com/public/deal-slide-1.jpg';
    
    // Return appropriate background based on slide ID
    switch (slideId) {
      case 'intro':
        return deal?.header_image_url || defaultBackground;
      case 'business':
        return 'https://findbob-production.s3.amazonaws.com/public/deal-slide-2.jpg';
      case 'financials':
        return 'https://findbob-production.s3.amazonaws.com/public/deal-slide-3.jpg';
      case 'clients':
        return 'https://findbob-production.s3.amazonaws.com/public/deal-slide-4.jpg';
      case 'products':
        return 'https://findbob-production.s3.amazonaws.com/public/deal-slide-5.jpg';
      default:
        return defaultBackground;
    }
  };
  
  // Just signed notification
  const renderJustSignedNotification = () => {
    if (!justSigned) return null;
    
    return (
      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm leading-5 text-green-700">
              <FormattedMessage id="succession_deals.deal_teaser.notifications.nda_signed" />
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  // Slides for the teaser carousel
  const slides = [
    { id: 'title', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.title' }) },
    { id: 'business-highlights', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.business_highlights' }) },
    { id: 'summary', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.summary' }) },
    { id: 'ideal-candidate', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.ideal_candidate' }) },
    { id: 'financial-highlights', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.financial_highlights' }) },
    { id: 'client-demographics', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.client_demographics' }) },
    { id: 'products-services', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.products_services' }) },
    { id: 'growth-opportunities', title: intl.formatMessage({ id: 'succession_deals.deal_teaser.slides.growth_opportunities' }) }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const renderSlideContent = () => {
    const slide = slides[currentSlide];
    
    switch (slide.id) {
      case 'title':
        return (
          <div className="flex flex-col justify-center h-full relative p-8 text-white">
            <div className="absolute top-8 left-8 text-sm">{`#${deal?.deal_name || 'Unknown'}`}</div>
            
            <div className="max-w-xl mx-auto text-center">
              <h1 className="text-xl font-bold mb-8" style={{ paddingTop: '30%'}}>{deal?.headline || 'Deal Information'}</h1>
              {!companySettings.teaserHideBusinessOverviewQuestion && (
                <h2 className="text-lg">{deal?.sub_headline}</h2>
              )}
            </div>
          </div>
        );
        
      case 'business-highlights':
        return (
          <div className="flex flex-col justify-center h-full relative p-8 text-white">
            <div className="absolute top-8 left-8 text-sm">{`#${deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}`}</div>
            
            <div className="mb-6 text-center">
              <h1 className="text-4xl font-bold">
                <FormattedMessage id="succession_deals.deal_teaser.slides.business_highlights" />
              </h1>
            </div>
            
            <div className="grid grid-cols-2 gap-x-16 gap-y-4 text-sm max-w-4xl mx-auto">
              {!hideListPrice && (
                <>
                  <div className="font-semibold text-right">
                    <FormattedMessage id="succession_deals.deal_teaser.business_highlights.list_price" />:
                  </div>
                  <div className="font-normal">
                    {deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.tbd' })}
                  </div>
                </>
              )}
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.location_service_area" />:
              </div>
              <div className="font-normal">{deal.inventory_user?.user?.city}, {deal.inventory_user?.user?.province}</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.number_of_clients" />:
              </div>
              <div className="font-normal">{deal.inventory_user?.user?.number_of_clients || '3'}</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.year_established" />:
              </div>
              <div className="font-normal">{
                deal.inventory_user?.user?.years_practicing ? 
                  (new Date().getFullYear() - deal.inventory_user.user.years_practicing).toString() : '-'
               }</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.commission" />:
              </div>
              <div className="font-normal">{deal.inventory_user?.user?.percent_commission ? `${Math.round(deal.inventory_user?.user?.percent_commission)}${intl.locale === 'fr' ? '\u00a0%' : '%'}` : '-'}</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.fee" />:
              </div>
              <div className="font-normal">{deal.inventory_user?.user?.percent_fee_based ? `${Math.round(deal.inventory_user?.user?.percent_fee_based)}${intl.locale === 'fr' ? '\u00a0%' : '%'}` : '-'}</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.team_size" />:
              </div>
              <div className="font-normal">{getData('team_size') || '2'}</div>
              
              <div className="font-semibold text-right">
                <FormattedMessage id="succession_deals.deal_teaser.business_highlights.business_continuity_plan" />:
              </div>
              <div className="font-normal">
                {getData('has_business_continuity_plan') ? 
                  intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.yes' }) : 
                  intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.no' })
                }
              </div>
              
            </div>
          </div>
        );
        
      case 'summary':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id="succession_deals.deal_teaser.slides.summary" />
            </h1>
            <p className="text-base max-w-4xl mx-auto">
              {deal?.summary_of_business || intl.formatMessage({ id: 'succession_deals.deal_teaser.content.no_summary_available' })}
            </p>
          </div>
        );
        
      case 'ideal-candidate':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id="succession_deals.deal_teaser.slides.ideal_candidate" />
            </h1>
            <p className="text-base max-w-4xl mx-auto">
              {deal?.ideal_candidate || intl.formatMessage({ id: 'succession_deals.deal_teaser.content.no_ideal_candidate' })}
            </p>
          </div>
        );
        
      case 'financial-highlights':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id="succession_deals.deal_teaser.slides.financial_highlights" />
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <h4 className="text-lg font-semibold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.key_metrics" />
              </h4>
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm mb-4">
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.assets_under_management" />:
                </div>
                <div>{formatCurrency(getData('assets_under_management'))}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.gross_margin" />:
                </div>
                <div>{getData('gross_margin') ? `${getData('gross_margin')}${intl.locale === 'fr' ? '\u00a0%' : '%'}` : 'N/A'}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.operating_margin" />:
                </div>
                <div>{getData('operating_margin') ? `${getData('operating_margin')}${intl.locale === 'fr' ? '\u00a0%' : '%'}` : 'N/A'}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.net_income" />:
                </div>
                <div>{formatCurrency(getData('net_income'))}</div>
              </div>
              
              <h4 className="text-lg font-semibold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.recent_revenue" />
              </h4>
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm mb-4">
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.ytd_gross" />:
                </div>
                <div>{formatCurrency(getData('year_to_date_gross'))}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.trailing_3_months" />:
                </div>
                <div>{formatCurrency(getData('trailing_3_months_gross'))}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.trailing_6_months" />:
                </div>
                <div>{formatCurrency(getData('trailing_6_months_gross'))}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.trailing_12_months" />:
                </div>
                <div>{formatCurrency(getData('trailing_12_months_gross'))}</div>
              </div>
              
              <h4 className="text-lg font-semibold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.annual_revenue" />
              </h4>
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm mb-4">
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.t12_revenue" />:
                </div>
                <div>{formatCurrency(deal.inventory_user?.user?.gross_production)}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.t12_recurring_revenue" />:
                </div>
                <div>{formatCurrency(deal.inventory_user?.user?.recurring_revenue)}</div>
              </div>
              
              {/* Only show historical gross production if there's data */}
              {(deal.inventory_user?.user?.year_1_gross > 0 || 
                deal.inventory_user?.user?.year_2_gross > 0 || 
                deal.inventory_user?.user?.year_3_gross > 0 || 
                deal.inventory_user?.user?.year_4_gross > 0) && (
              <>
              <h4 className="text-lg font-semibold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.financial_highlights.gross_production" />
              </h4>
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm">
                <div className="font-semibold text-right">{new Date().getFullYear() - 1}:</div>
                <div>{formatCurrency(deal.inventory_user?.user?.year_1_gross)}</div>
                
                <div className="font-semibold text-right">{new Date().getFullYear() - 2}:</div>
                <div>{formatCurrency(deal.inventory_user?.user?.year_2_gross)}</div>
                
                <div className="font-semibold text-right">{new Date().getFullYear() - 3}:</div>
                <div>{formatCurrency(deal.inventory_user?.user?.year_3_gross)}</div>
                
                <div className="font-semibold text-right">{new Date().getFullYear() - 4}:</div>
                <div>{formatCurrency(deal.inventory_user?.user?.year_4_gross)}</div>
              </div>
              </>
              )}
            </div>
          </div>
        );
        
      case 'client-demographics':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id={companySettings.businessType === 'wealth_rj' ? 
                'succession_deals.deal_teaser.slides.client_demographics' : 
                'succession_deals.deal_teaser.slides.customer_demographics'} />
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-x-16 gap-y-3 text-sm">
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.client_demographics.number_of_clients" />:
                </div>
                <div>{deal?.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.client_demographics.average_client_age" />:
                </div>
                <div>{deal?.inventory_user?.user?.avg_client_age || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</div>
                
                <div className="font-semibold text-right">
                  <FormattedMessage id="succession_deals.deal_teaser.client_demographics.niche_market" />:
                </div>
                <div>{deal?.inventory_user?.user?.niche_market || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.no' })}</div>
              </div>
              
              <h3 className="text-xl font-semibold mt-6 mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.client_demographics.additional_comments" />
              </h3>
              <p>{deal?.customer_demographics_comments || intl.formatMessage({ id: 'succession_deals.deal_teaser.client_demographics.no_additional_comments' })}</p>
            </div>
          </div>
        );
        
      case 'products-services':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id="succession_deals.deal_teaser.slides.products_services" />
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">
                  <FormattedMessage id="succession_deals.deal_teaser.products_services.service_offerings" />
                </h3>
                <div className="flex flex-wrap gap-2">
                  {deal?.inventory_user?.user?.tag_list ? 
                    split(get(deal, 'inventory_user.user.tag_list', []), ',').map((tag, index) => (
                        <span key={index} className="px-3 py-1 bg-succession-primary text-white rounded-full text-sm">
                          {tag.trim()}
                        </span>
                      ))
                    : 
                    <span className="text-sm italic">
                      <FormattedMessage id="succession_deals.deal_teaser.products_services.no_service_offerings" />
                    </span>
                  }
                </div>
              </div>
              
              <h3 className="text-xl font-semibold mb-2">
                <FormattedMessage id="succession_deals.deal_teaser.products_services.additional_comments" />
              </h3>
              <p>{deal?.products_and_services_comments || intl.formatMessage({ id: 'succession_deals.deal_teaser.products_services.no_additional_comments' })}</p>
            </div>
          </div>
        );
        
      case 'growth-opportunities':
        return (
          <div className="flex flex-col justify-center h-full p-8 text-white">
            <p className="text-sm mb-2">#{deal?.deal_name || intl.formatMessage({ id: 'succession_deals.deal_teaser.business_highlights.unknown' })}</p>
            <h1 className="text-3xl font-bold mb-4 text-center">
              <FormattedMessage id="succession_deals.deal_teaser.slides.growth_opportunities" />
            </h1>
            <p className="text-base max-w-4xl mx-auto">{deal?.growth_opportunities || intl.formatMessage({ id: 'succession_deals.deal_teaser.content.no_growth_opportunities' })}</p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  const renderCashFlowAnalysis = () => {
    // Remove this section as cashFlows isn't in the schema
    return null;
  };
  
  const renderExpressionsOfInterest = () => {
    if (!dealInterest || isDealOwner) return null;
    
    return (
      <div className="bg-succession-bg-primary shadow rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-succession-text-primary">
          <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.title" />
        </h2>
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.question_1" />
          </h3>
          <p className="bg-succession-bg-secondary p-4 rounded">{dealInterest.a1 || intl.formatMessage({ id: 'succession_deals.deal_teaser.expressions_of_interest.not_provided' })}</p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.question_2" />
          </h3>
          <p className="bg-succession-bg-secondary p-4 rounded">{dealInterest.a2 || intl.formatMessage({ id: 'succession_deals.deal_teaser.expressions_of_interest.not_provided' })}</p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.question_3" />
          </h3>
          <p className="bg-succession-bg-secondary p-4 rounded">{dealInterest.a3 || intl.formatMessage({ id: 'succession_deals.deal_teaser.expressions_of_interest.not_provided' })}</p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.question_4" />
          </h3>
          <p className="bg-succession-bg-secondary p-4 rounded">{dealInterest.a4 || intl.formatMessage({ id: 'succession_deals.deal_teaser.expressions_of_interest.not_provided' })}</p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">
            <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.question_5" />
          </h3>
          <p className="bg-succession-bg-secondary p-4 rounded">{dealInterest.a5 || intl.formatMessage({ id: 'succession_deals.deal_teaser.expressions_of_interest.not_provided' })}</p>
        </div>
        
        {dealInterest.proposal_letter && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">
              <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.proposal_letter" />
            </h3>
            <div className="bg-succession-bg-secondary p-4 rounded">
              <p>{dealInterest.proposal_letter}</p>
            </div>
          </div>
        )}
        
        {dealInterest.proposal_meeting_availability && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">
              <FormattedMessage id="succession_deals.deal_teaser.expressions_of_interest.meeting_availability" />
            </h3>
            <div className="bg-succession-bg-secondary p-4 rounded">
              <p>{dealInterest.proposal_meeting_availability}</p>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  const renderDocuments = () => {
    if (documents.length === 0 || isDealOwner) return null;
    
    return (
      <div className="bg-succession-bg-primary shadow rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-succession-text-primary">
          <FormattedMessage id="succession_deals.deal_teaser.documents.title" />
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-succession-border">
            <thead>
              <tr className="bg-succession-bg-secondary">
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                  <FormattedMessage id="succession_deals.deal_teaser.documents.document_header" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                  <FormattedMessage id="succession_deals.deal_teaser.documents.size_header" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                  <FormattedMessage id="succession_deals.deal_teaser.documents.date_header" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                  <FormattedMessage id="succession_deals.deal_teaser.documents.action_header" />
                </th>
              </tr>
            </thead>
            <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-primary">{doc.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.documents.unknown_size" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-secondary">
                    {new Date(doc.created_at).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <a 
                      href={doc.download_url} 
                      target="_blank"
                      rel="noopener noreferrer" 
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FormattedMessage id="succession_deals.deal_teaser.documents.download_action" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  const renderActionButtons = () => {
    // Default - no actions available
    return <div>
      <br/>
    </div>;
  };
  
  const renderLenderOffers = () => {
    if (!lenderOfferCTAsEnabled || eligibleOffers.length === 0) return null;
    
    return (
      <div className="bg-succession-bg-primary shadow rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-succession-text-primary">
          <FormattedMessage id="succession_deals.deal_teaser.financing.title" />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {eligibleOffers.map(lender => (
            <div key={lender.id} className="border rounded-lg p-4 flex flex-col">
              <div className="flex items-center mb-4">
                {lender.logo ? (
                  <img src={lender.logo} alt={lender.name} className="h-12" />
                ) : (
                  <div className="font-semibold text-lg">{lender.name}</div>
                )}
              </div>
              <p className="text-sm text-succession-text-secondary mb-4">{lender.description}</p>
              <div className="mt-auto">
                <a 
                  href={`/loans/calculator?lender_id=${lender.id}&deal_id=${deal?.id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover"
                >
                  <FormattedMessage id="succession_deals.deal_teaser.financing.see_options" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderLenderInquiry = () => {
    if (!lenderInquiryCTAsEnabled || !randomLender) return null;
    
    return (
      <div className="bg-succession-primary-light border border-succession-border rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center">
          <div className="mb-4 md:mb-0 md:mr-6 flex-grow">
            <h3 className="text-lg font-medium text-succession-primary mb-1">
              <FormattedMessage id="succession_deals.deal_teaser.lender_inquiry.title" />
            </h3>
            <p className="text-sm text-succession-primary">
              <FormattedMessage 
                id="succession_deals.deal_teaser.lender_inquiry.description" 
                values={{ lenderName: randomLender.name }}
              />
            </p>
          </div>
          <div className="flex-shrink-0">
            <a 
              href={`/loans/interested_in_financing?lender_id=${randomLender.id}&deal_id=${deal?.id}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover"
            >
              <FormattedMessage id="succession_deals.deal_teaser.lender_inquiry.learn_more" />
            </a>
          </div>
        </div>
      </div>
    );
  };
  
  // Raymond James logo component
  const RJLogo = () => (
    <div className="absolute bottom-8 right-8 text-white z-20">
      <div className="text-xl font-bold tracking-wide">RAYMOND JAMES<sup>®</sup></div>
      <div className="text-sm tracking-wider">PRACTICE EXCHANGE</div>
    </div>
  );
  
  // Confidential watermark
  const ConfidentialWatermark = () => (
    <div className="absolute bottom-8 left-8 text-white text-sm font-medium z-20">
      <FormattedMessage id="succession_deals.deal_teaser.confidential_watermark" />
    </div>
  );

  // Breadcrumbs component
  const renderBreadcrumbs = () => {
    return (
      <span></span>
    );
  };

  // Deal header with status
  const renderDealHeader = () => {
    if (isDealOwner) return null;
    
    return (
      <div className="bg-succession-bg-primary border-b border-succession-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-succession-text-primary">
                #{deal?.deal_name} - <FormattedMessage id="succession_deals.deal_teaser.teaser_title" />
                {deal?.closed && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-error-bg text-succession-error">
                    <FormattedMessage id="succession_deals.deal_teaser.status.closed" />
                  </span>
                )}
              </h1>
            </div>
            {dealInterest?.proposal === false && dealInterest?.proposal_deadline && (
              <Link
                to={`/deal/proposal?deal_id=${deal?.deal_name}&match_id=${dealInterest?.deal_interest_name}`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-succession-error hover:bg-succession-error"
              >
                <FormattedMessage 
                  id="succession_deals.deal_teaser.proposal.deadline"
                  values={{ date: new Date(dealInterest.proposal_deadline).toLocaleDateString() }}
                />
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Deal Room Menu for owners
  const renderDealRoomMenu = () => {
    if (!isDealOwner) return null;
    
    return (
      <span></span>
    );
  };

  // Approval workflow banners
  const renderApprovalBanners = () => {
    if (!isDealOwner) return null;
    
    if (!deal?.approval_status && deal?.published) {
      return (
        <div className="bg-succession-primary-light border-l-4 border-succession-primary p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-succession-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-succession-primary">
                  <FormattedMessage id="succession_deals.deal_teaser.approval.provide_feedback_title" />
                </h3>
                <p className="mt-2 text-sm text-succession-primary">
                  <FormattedMessage id="succession_deals.deal_teaser.approval.provide_feedback_description" />
                </p>
              </div>
            </div>
            <button
              onClick={() => {/* Handle approval */}}
              className="bg-succession-primary hover:bg-succession-primary-hover text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <FormattedMessage id="succession_deals.deal_teaser.approval.approve_teaser_button" />
            </button>
          </div>
        </div>
      );
    } else if (deal?.approval_status && deal?.published) {
      return (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                <FormattedMessage id="succession_deals.deal_teaser.approval.approved_title" />
              </h3>
              <p className="mt-2 text-sm text-green-700">
                <FormattedMessage id="succession_deals.deal_teaser.approval.approved_description" />
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Check if deal is advanced (NDA signed)
  const isDealAdvanced = () => {
    return dealInterest && ['awaiting_disclosing_signature', 'negotiating'].includes(dealInterest?.workflow_state);
  };

  // Seller profile when NDA is signed
  const renderSellerProfile = () => {
    if (!isDealAdvanced()) return null;
    
    const seller = deal?.inventory_user;
    if (!seller) return null;
    const avatarStyle = getAvatarStyle(seller?.user?.profile_picture, companySettings);
    
    const formatCurrency = (amount) => {
      if (!amount || isNaN(amount) || !isFinite(amount)) return '-';
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 0
      }).format(amount);
    };
    
    return (
      <div className="bg-white shadow-sm rounded-lg mb-6 border border-gray-200">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div
              className="flex-shrink-0 h-16 w-16 rounded-full overflow-hidden"
              style={{ backgroundColor: avatarStyle.backgroundColor }}
            >
              <img 
                className="h-16 w-16 rounded-full object-cover"
                src={avatarStyle.url}
                alt={seller?.name}
                style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-medium text-succession-text-primary">
                    {seller?.name || seller?.user?.user_name}
                  </p>
                  <p className="text-sm text-succession-text-secondary truncate">{seller?.user?.headline}</p>
                  <p className="text-sm text-succession-text-secondary">
                    <svg className="inline h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {seller?.user?.city}, {seller?.user?.province}
                  </p>
                </div>
                <button className="bg-succession-primary hover:bg-succession-primary-hover text-white px-4 py-2 rounded-md text-sm font-medium">
                  <FormattedMessage id="succession_deals.deal_teaser.seller_profile.view_profile" />
                </button>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-medium text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.seller_profile.annual_revenue" />
                  </dt>
                  <dd className="mt-1 text-succession-text-primary">{formatCurrency(seller?.user?.gross_production)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.seller_profile.recurring_revenue" />
                  </dt>
                  <dd className="mt-1 text-succession-text-primary">{formatCurrency(seller?.user?.recurring_revenue)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.seller_profile.assets_under_management" />
                  </dt>
                  <dd className="mt-1 text-succession-text-primary">{formatCurrency(seller?.user?.assets_under_management)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.seller_profile.number_of_clients" />
                  </dt>
                  <dd className="mt-1 text-succession-text-primary">{seller?.user?.number_of_clients}</dd>
                </div>
                <div>
                  <dt className="font-medium text-succession-text-secondary">
                    <FormattedMessage id="succession_deals.deal_teaser.seller_profile.years_in_industry" />
                  </dt>
                  <dd className="mt-1 text-succession-text-primary">{seller?.user?.years_practicing}</dd>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Messaging component for when NDA is signed
  const renderMessaging = () => {
    if (!isDealAdvanced()) return null;
    
    return (
      <div id="teaser-messaging" className="bg-succession-bg-primary shadow-sm rounded-lg mb-6 border border-succession-border">
        <div className="px-6 py-4 border-b border-succession-border">
          <h3 className="text-lg font-medium text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.messaging.title' })}</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-succession-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4L3 20l4-4C3.582 16 0 12.418 0 8c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.messaging.no_messages_yet' })}</h3>
              <p className="mt-1 text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'dealTeaser.messaging.start_conversation' })}</p>
              <div className="mt-6">
                <button className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-succession-primary hover:bg-succession-primary-hover">
                  {intl.formatMessage({ id: 'dealTeaser.messaging.send_message' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Document sharing section for advanced deals
  const renderSharedDocuments = () => {
    if (!isDealAdvanced()) return null;
    
    return (
      <div className="bg-succession-bg-primary shadow-sm rounded-lg mb-6 border border-succession-border">
        <div className="px-6 py-4 border-b border-succession-border">
          <h3 className="text-lg font-medium text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.shared_documents.title' })}</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {dealInterest?.documents?.length > 0 ? (
              dealInterest.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 border border-succession-border rounded-lg">
                  <div className="flex items-center">
                    <svg className="h-8 w-8 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-succession-text-primary">{doc.name}</p>
                      <p className="text-sm text-succession-text-secondary">
                        {intl.formatMessage({ id: 'dealTeaser.shared_documents.shared_on' }, { date: new Date(doc.created_at).toLocaleDateString() })}
                      </p>
                    </div>
                  </div>
                  <a
                    href={doc.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-2 border border-succession-border shadow-sm text-sm font-medium rounded-md text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-hover-bg"
                  >
                    {intl.formatMessage({ id: 'dealTeaser.shared_documents.download' })}
                  </a>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-succession-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.shared_documents.no_documents_yet' })}</h3>
                <p className="mt-1 text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'dealTeaser.shared_documents.documents_will_appear' })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Enhanced cash flow analysis
  const renderEnhancedCashFlowAnalysis = () => {
    if (!isDealAdvanced()) return null;
    
    return (
      <div className="bg-succession-bg-primary shadow-sm rounded-lg mb-6 border border-succession-border">
        <div className="px-6 py-4 border-b border-succession-border">
          <h3 className="text-lg font-medium text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.title' })}</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-succession-bg-secondary rounded-lg p-4">
              <div className="text-2xl font-bold text-succession-text-primary">
                {deal?.inventory_user?.user?.recurring_revenue && !isNaN(deal.inventory_user.user.recurring_revenue) ? `${(deal.inventory_user.user.recurring_revenue / 1000).toFixed(0)}K` : '-'}
              </div>
              <div className="text-sm text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.monthly_recurring_revenue' })}</div>
            </div>
            <div className="bg-succession-bg-secondary rounded-lg p-4">
              <div className="text-2xl font-bold text-succession-text-primary">
                {deal?.inventory_user?.user?.gross_production && !isNaN(deal.inventory_user.user.gross_production) ? `${(deal.inventory_user.user.gross_production / 1000).toFixed(0)}K` : '-'}
              </div>
              <div className="text-sm text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.annual_revenue' })}</div>
            </div>
            <div className="bg-succession-bg-secondary rounded-lg p-4">
              <div className="text-2xl font-bold text-succession-text-primary">
                {deal?.inventory_user?.user?.assets_under_management && !isNaN(deal.inventory_user.user.assets_under_management) ? `${(deal.inventory_user.user.assets_under_management / 1000000).toFixed(1)}M` : '-'}
              </div>
              <div className="text-sm text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.assets_under_management' })}</div>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="text-sm font-medium text-succession-text-primary mb-4">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.key_metrics' })}</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.number_of_clients' })}</span>
                <span className="text-sm font-medium text-succession-text-primary">{deal?.inventory_user?.user?.number_of_clients || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.average_client_size' })}</span>
                <span className="text-sm font-medium text-succession-text-primary">
                  {deal?.inventory_user?.user?.assets_under_management && deal?.inventory_user?.user?.number_of_clients && deal.inventory_user.user.number_of_clients > 0
                    ? `${currencySymbol()}${(deal.inventory_user.user.assets_under_management / deal.inventory_user.user.number_of_clients / 1000).toFixed(0)}K`
                    : '-'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'dealTeaser.financial_analysis.annual_fee_rate' })}</span>
                <span className="text-sm font-medium text-succession-text-primary">
                  {deal?.inventory_user?.user?.gross_production && deal?.inventory_user?.user?.assets_under_management && deal.inventory_user.user.assets_under_management > 0
                    ? `${((deal.inventory_user.user.gross_production / deal.inventory_user.user.assets_under_management) * 100).toFixed(2)}${intl.locale === 'fr' ? '\u00a0%' : '%'}`
                    : '-'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render the top navigation tabs
  const renderNavTabs = () => {
    // Determine current user state
    const isDealInterest = dealInterest !== null;
    const isInvited = isDealInterest && dealInterest.workflow_state === 'invited';
    const isReviewingProposal = isDealInterest && dealInterest.workflow_state === 'reviewing_proposal';
    const isShortlisted = isDealInterest && dealInterest.workflow_state === 'shortlisted';
    const isOutForSignature = isDealInterest && dealInterest.workflow_state === 'out_for_receiving_signature';
    const isAwaitingSignature = isDealInterest && dealInterest.workflow_state === 'awaiting_disclosing_signature';
    const isNegotiating = isDealInterest && dealInterest.workflow_state === 'negotiating';
    
    const isStep1Complete = isInvited || isReviewingProposal || isShortlisted || isOutForSignature || isAwaitingSignature || isNegotiating;
    const isStep2Complete = isReviewingProposal || isShortlisted || isOutForSignature || isAwaitingSignature || isNegotiating;
    const isStep3Complete = isOutForSignature || isAwaitingSignature || isNegotiating;
    const isStep4Complete = isNegotiating;
    
    // Function to navigate to deal page
    const navigateToDealPage = (page) => {
      window.location.href = `/succession/deals/${deal?.deal_name}/${page}`;
    };
    
    // Helper to safely access deal properties with fallbacks
    const safeCount = (property, defaultValue = 0) => {
      if (!deal) return defaultValue;
      return deal[property] !== undefined ? deal[property] : 
             Array.isArray(deal[property]) ? deal[property].length : defaultValue;
    };
    
    if (isDealOwner) {
      // Define the navigation tabs for deal owner
      const tabs = [
        { 
          id: 'teaser', 
          tutorialId: 'deal-teaser-tab-teaser',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.teaser.label', defaultMessage: 'View Teaser' }),
          count: undefined,
          description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.teaser.description', defaultMessage: 'View the deal teaser' }),
          isActive: true // This tab is active since we're on the teaser page
        },
        { 
          id: 'matches', 
          tutorialId: 'deal-teaser-tab-matches',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.matches.label', defaultMessage: 'Matches' }),
          count: safeCount('matches_count', 0),
          description: intl.formatMessage(
            {
              id: 'succession_deals.wizard.steps.deal_prospects.tabs.matches.description_with_agent_word',
              defaultMessage: '{agentWordPlural} matches'
            },
            { agentWordPlural: agentWordPluralLower }
          ),
          isActive: false
        },
        { 
          id: 'invited', 
          tutorialId: 'deal-teaser-tab-invited',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.invited.label', defaultMessage: 'Invited' }),
          count: safeCount('number_of_invites', 0),
          description: intl.formatMessage(
            {
              id: 'succession_deals.wizard.steps.deal_prospects.tabs.invited.description_with_agent_word',
              defaultMessage: 'Invited {agentWordPlural}'
            },
            { agentWordPlural: agentWordPluralLower }
          ),
          isActive: false
        },
        { 
          id: 'reviewing', 
          tutorialId: 'deal-teaser-tab-reviewing',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.reviewing.label', defaultMessage: 'Reviewing' }),
          count: safeCount('number_of_proposals', 0),
          description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.reviewing.description', defaultMessage: 'Reviewing proposals' }),
          isActive: false
        },
        { 
          id: 'shortlisted', 
          tutorialId: 'deal-teaser-tab-shortlisted',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.shortlisted.label', defaultMessage: 'Shortlisted' }),
          count: deal?.shortlisted ? deal.shortlisted.length : 0,
          description: intl.formatMessage(
            {
              id: 'succession_deals.wizard.steps.deal_prospects.tabs.shortlisted.description_with_agent_word',
              defaultMessage: 'Shortlisted {agentWordPlural}'
            },
            { agentWordPlural: agentWordPluralLower }
          ),
          isActive: false
        },
        { 
          id: 'ndas', 
          tutorialId: 'deal-teaser-tab-ndas',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.ndas.label', defaultMessage: 'NDAs' }),
          count: safeCount('number_of_ndas', 0),
          description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.ndas.description', defaultMessage: 'NDA status' }),
          isActive: false
        },
        { 
          id: 'negotiate', 
          tutorialId: 'deal-teaser-tab-negotiate',
          label: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.negotiate.label', defaultMessage: 'Negotiate' }),
          count: safeCount('number_of_negotiations', 0),
          description: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_prospects.tabs.negotiate.description', defaultMessage: 'Negotiations' }),
          isActive: false
        }
      ];

      // Handle tab changes
      const handleTabChange = (tabId) => {
        if (tabId === 'teaser') {
          window.location.href = `/succession/deals/${deal.deal_name}/teaser`;
        } else {
          window.location.href = `/succession/deals/${deal.deal_name}/prospects/${tabId}`;
        }
      };

      return (
        <ArrowTabs
          tabs={tabs}
          onTabChange={handleTabChange}
        />
      );
    } else if (isDealInterest) {
      // Buyer workflow progress tabs with interactive elements
      return (
        <div className="bg-succession-bg-primary rounded-lg shadow-sm overflow-hidden">
            <div className="flex border-b border-succession-border">
              <div 
                className={`flex-1 px-4 py-3 flex items-center justify-center ${isStep1Complete ? 'bg-succession-bg-light' : 'bg-succession-bg-primary'} cursor-pointer transition-colors duration-150 relative`}
                onClick={() => isStep1Complete && navigateToDealPage('express-interest')}
              >
                <div className="flex items-center">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${isStep1Complete ? 'bg-succession-success' : 'bg-succession-primary'} text-white`}>
                    {isStep1Complete ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    ) : (
                      <span>1</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${isStep1Complete ? 'text-succession-success' : 'text-succession-text-secondary'}`}>
                    <FormattedMessage id="dealTeaser.progressSteps.expressInterest" />
                  </span>
                </div>
                <div className="absolute right-0 top-0 h-full w-4 flex items-center justify-center">
                  <svg className="h-full text-succession-border" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                    <path d="M0 0L22 40L0 80" stroke="currentColor" />
                  </svg>
                </div>
              </div>
              
              <div 
                className={`flex-1 px-4 py-3 flex items-center justify-center ${isStep2Complete ? 'bg-succession-bg-light' : 'bg-succession-bg-primary'} cursor-pointer transition-colors duration-150 relative`}
                onClick={() => isStep2Complete && navigateToDealPage('eoi-review')}
              >
                <div className="flex items-center">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${isStep2Complete ? 'bg-succession-success' : isStep1Complete ? 'bg-succession-warning' : 'bg-succession-text-muted'} text-white`}>
                    {isStep2Complete ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    ) : (
                      <span>2</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${isStep2Complete ? 'text-succession-success' : isStep1Complete ? 'text-succession-text-primary' : 'text-succession-text-muted'}`}>
                    <FormattedMessage id="dealTeaser.progressSteps.eoiReview" />
                  </span>
                </div>
                <div className="absolute right-0 top-0 h-full w-4 flex items-center justify-center">
                  <svg className="h-full text-succession-border" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                    <path d="M0 0L22 40L0 80" stroke="currentColor" />
                  </svg>
                </div>
              </div>
              
              <div 
                className={`flex-1 px-4 py-3 flex items-center justify-center ${isStep3Complete ? 'bg-succession-bg-light' : 'bg-succession-bg-primary'} cursor-pointer transition-colors duration-150 relative`}
                onClick={() => isStep3Complete && navigateToDealPage('shortlisted')}
              >
                <div className="flex items-center">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${isStep3Complete ? 'bg-succession-success' : isStep2Complete ? 'bg-succession-warning' : 'bg-succession-text-muted'} text-white`}>
                    {isStep3Complete ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    ) : (
                      <span>3</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${isStep3Complete ? 'text-succession-success' : isStep2Complete ? 'text-succession-text-primary' : 'text-succession-text-muted'}`}>
                    <FormattedMessage id="dealTeaser.progressSteps.shortlisted" />
                  </span>
                </div>
                <div className="absolute right-0 top-0 h-full w-4 flex items-center justify-center">
                  <svg className="h-full text-succession-border" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                    <path d="M0 0L22 40L0 80" stroke="currentColor" />
                  </svg>
                </div>
              </div>
              
              <div 
                className={`flex-1 px-4 py-3 flex items-center justify-center ${isStep4Complete ? 'bg-succession-bg-light' : 'bg-succession-bg-primary'} cursor-pointer transition-colors duration-150`}
                onClick={() => isStep4Complete && navigateToDealPage('negotiation')}
              >
                <div className="flex items-center">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${isStep4Complete ? 'bg-succession-success' : isStep3Complete ? 'bg-succession-warning' : 'bg-succession-text-muted'} text-white`}>
                    {isStep4Complete ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    ) : (
                      <span>4</span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${isStep4Complete ? 'text-succession-success' : isStep3Complete ? 'text-succession-text-primary' : 'text-succession-text-muted'}`}>
                    <FormattedMessage id="dealTeaser.progressSteps.negotiate" />
                  </span>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-1 bg-succession-bg-secondary">
              <div 
                className="h-full bg-succession-success transition-all duration-300"
                style={{ 
                  width: `${isStep4Complete ? '100' : isStep3Complete ? '75' : isStep2Complete ? '50' : isStep1Complete ? '25' : '0'}%` 
                }}
              ></div>
            </div>
          </div>
      );
    } else {
      // Simple default tabs for non-owners without deal interest
      return (
          <div className="flex border-b border-succession-border">
            <div className="px-6 py-3 font-medium text-sm rounded-t-md border-t border-l border-r border-succession-border bg-succession-primary text-white -mb-px">
              {intl.formatMessage({ id: 'dealTeaser.tabs.view_teaser' })}
            </div>

            <button
              onClick={() => navigateToDealPage('express-interest')}
              className="px-6 py-3 font-medium text-sm rounded-t-md border-t border-l border-r border-succession-border bg-succession-bg-light text-succession-text-secondary hover:bg-succession-bg-secondary transition-colors duration-150 ml-1"
            >
              {intl.formatMessage({ id: 'dealTeaser.tabs.express_interest' })}
            </button>
          </div>
      );
    }
  };
  
  return (
    <>
      {/* Deal Header for non-owners */}
      {renderDealHeader()}
      
      {/* Approval workflow banners */}
      {renderApprovalBanners()}
      
      {/* Main content wrapper */}
      <div className="mx-auto px-4 py-6" style={{ maxWidth: '66rem' }}>
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-succession-text-primary mb-1">
            {deal?.headline || intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_teaser.teaser_title' })}
          </h1>
          <p className="text-succession-text-secondary">#{deal?.deal_name || ''}</p>
        </div>
        
        {/* Nav Tabs */}
        <Card className="p-3 mb-6">
          {renderNavTabs()}
        </Card>
        {/* Just signed notification */}
        {renderJustSignedNotification()}
        
        {/* Seller Profile (when NDA signed) */}
        {renderSellerProfile()}
        
        {/* Enhanced Cash Flow Analysis (when NDA signed) */}
        {renderEnhancedCashFlowAnalysis()}
        
        {/* Messaging (when NDA signed) */}
        {renderMessaging()}
        
        {/* Documents (when NDA signed) */}
        {renderSharedDocuments()}
        
        {/* Lender Offers (when NDA signed) */}
        {renderLenderOffers && renderLenderOffers()}
        
        {/* Deal header section for owners */}
        {isDealOwner && (
          <div className="bg-succession-bg-primary p-6 rounded-2xl shadow-sm mb-8 border border-succession-border">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
                <h1 className="text-2xl font-light text-succession-text-primary mb-2">{intl.formatMessage({ id: 'dealTeaser.owner_section.title' })}</h1>
                <p className="text-sm text-succession-text-secondary leading-relaxed">
                  {intl.formatMessage(
                    {
                      id: 'succession_deals.deal_teaser.owner_preview.description_with_agent_word',
                      defaultMessage: 'This is what {agentWordPlural} will see about your opportunity when invited to view and express interest.'
                    },
                    { agentWordPlural: agentWordPluralLower }
                  )}
              </p>
            </div>
              <div className="mt-6 md:mt-0 flex items-center space-x-3">
              <a
                href={`/succession/deals/wizard?mode=edit`}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover transition-all duration-200"
              >
                {intl.formatMessage({ id: 'dealTeaser.owner_section.edit_teaser' })}
              </a>
              <button
                onClick={() => setShowMatchPreferencesModal(true)}
                className="inline-flex items-center px-6 py-3 border border-succession-border text-sm font-medium rounded-xl text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-hover-bg transition-all duration-200"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <FormattedMessage
                  id="succession_deals.deal_teaser.match_preferences_button"
                  defaultMessage="Match Preferences"
                />
              </button>
              <button
                onClick={generatePDF}
                  className="inline-flex items-center px-6 py-3 border border-succession-border text-sm font-medium rounded-xl text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-hover-bg transition-all duration-200"
              >
                  <svg className="h-4 w-4 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                  {intl.formatMessage({ id: 'dealTeaser.owner_section.download_pdf' })}
              </button>
            </div>
          </div>
        </div>
        )}
        
        {/* Main Infographic Content */}
        <div id="deal-teaser-content" className="space-y-0">
          
          {/* Hero Investment Overview */}
          <div className="relative bg-succession-bg-primary">
            {/* Full-width hero image area */}
            <div className="relative h-screen bg-gradient-to-r from-gray-900 via-gray-800 to-black overflow-hidden">
              {/* Animated particles background */}
              <div className="absolute inset-0 pdf-hide">
                <div className="absolute top-10 left-10 w-2 h-2 bg-white/20 rounded-full animate-float delay-0"></div>
                <div className="absolute top-20 right-20 w-1 h-1 bg-white/30 rounded-full animate-float delay-500"></div>
                <div className="absolute top-40 left-1/3 w-1.5 h-1.5 bg-white/15 rounded-full animate-float delay-1000"></div>
                <div className="absolute top-60 right-1/4 w-2 h-2 bg-white/10 rounded-full animate-float delay-1500"></div>
                <div className="absolute bottom-40 left-10 w-1 h-1 bg-white/25 rounded-full animate-float delay-2000"></div>
                <div className="absolute bottom-20 right-40 w-1.5 h-1.5 bg-white/20 rounded-full animate-float delay-2500"></div>
                <div className="absolute top-80 left-2/3 w-1 h-1 bg-white/35 rounded-full animate-float delay-3000"></div>
                <div className="absolute bottom-60 right-10 w-2 h-2 bg-white/15 rounded-full animate-float delay-3500"></div>
              </div>
              {/* Animated gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 animate-gradient-shift"></div>
              <div className="absolute inset-0 bg-black/40"></div>
                            <div className="relative h-full flex items-center justify-center text-white">
                <div className="text-center max-w-5xl mx-auto px-8 animate-fade-in-up">
                  <div className="mb-8 opacity-0 animate-fade-in animation-delay-300">
                    <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur rounded-full text-sm font-medium tracking-wide border border-white/20 hover:bg-white/20 transition-all duration-500">
                      {intl.formatMessage({ id: 'dealTeaser.hero.badge' })}
                    </span>
                  </div>
                  <h1 className="text-7xl md:text-7xl font-thin mb-8 tracking-tight leading-none opacity-0 animate-fade-in animation-delay-500 hover:text-gray-100 transition-colors duration-700">
                    {deal?.headline || 'PRIME OPPORTUNITY'}
                  </h1>
                                    {deal?.sub_headline && !companySettings.teaserHideBusinessOverviewQuestion && (
                    <p className="text-2xl font-light opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed opacity-0 animate-fade-in animation-delay-700">
                      {deal.sub_headline}
                    </p>
                  )}
                  {!hideListPrice && (
                    <div className="text-6xl font-thin mb-4 opacity-0 animate-fade-in animation-delay-1000 hover:scale-105 transition-transform duration-500 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
                      <span className="relative animate-number-reveal">
                        {deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'dealTeaser.hero.price_on_application' })}
                      </span>
                    </div>
                  )}
                  <div className="text-lg font-light tracking-widest opacity-80 opacity-0 animate-fade-in animation-delay-1200">
                    {intl.formatMessage({ id: 'dealTeaser.hero.succession_opportunity_label' })} • {deal?.deal_name || 'CONFIDENTIAL'}
                  </div>
                </div>
              </div>
            </div>
          </div>

                    {/* Investment Summary */}
          <div className="bg-succession-bg-primary py-24" data-scroll-id="practice-overview">
            <div className="max-w-6xl mx-auto px-8">
              <div className="grid lg:grid-cols-3 gap-16 items-start">
                <div className={`lg:col-span-2 transition-all duration-1000 ${isVisible('practice-overview') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                  <h2 className="text-5xl font-thin mb-8 text-succession-text-primary hover:text-succession-primary transition-colors duration-500">{intl.formatMessage({ id: 'dealTeaser.practice_overview.title' })}</h2>
                  <div className="prose prose-xl max-w-none">
                    <p className="text-xl leading-relaxed text-succession-text-secondary font-light hover:text-succession-text-primary transition-colors duration-300">
                      {deal?.summary_of_business || intl.formatMessage({ id: 'dealTeaser.practice_overview.default_summary' })}
                    </p>
                  </div>
                </div>
                <div className={`bg-succession-bg-secondary p-8 rounded-none border-l-4 border-succession-primary hover:bg-succession-hover-bg hover:shadow-lg transition-all duration-1000 ${isVisible('practice-overview') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{transitionDelay: '200ms'}}>
                  <h3 className="text-2xl font-thin mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.practice_overview.key_metrics_title' })}</h3>
                  <div className="space-y-6">
                    <div>
                      <div className="text-sm font-medium text-succession-text-secondary uppercase tracking-wide mb-1">{intl.formatMessage({ id: 'dealTeaser.practice_overview.annual_revenue' })}</div>
                      <div className="text-3xl font-thin text-succession-text-primary">{formatCurrency(deal.inventory_user?.user?.gross_production)}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-succession-text-secondary uppercase tracking-wide mb-1">{intl.formatMessage({ id: 'dealTeaser.practice_overview.client_base' })}</div>
                      <div className="text-3xl font-thin text-succession-text-primary">{deal.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'dealTeaser.business_fundamentals.confidential' })}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-succession-text-secondary uppercase tracking-wide mb-1">{intl.formatMessage({ id: 'dealTeaser.practice_overview.established' })}</div>
                      <div className="text-3xl font-thin text-succession-text-primary">
                        {
                          deal.inventory_user?.user?.years_practicing ? 
                            (new Date().getFullYear() - deal.inventory_user.user.years_practicing).toString() : '-'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Fundamentals */}
          <div className="bg-gray-900 text-white py-24 relative overflow-hidden page-break-before" data-scroll-id="business-fundamentals">
            {/* Animated background patterns */}
            <div className="absolute inset-0 pdf-hide">
              <div className="absolute top-0 left-0 w-full h-full opacity-5">
                <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full animate-pulse-slow"></div>
                <div className="absolute top-40 right-20 w-24 h-24 border border-white/15 rounded-full animate-pulse-slow delay-1000"></div>
                <div className="absolute bottom-20 left-1/3 w-40 h-40 border border-white/10 rounded-full animate-pulse-slow delay-2000"></div>
                <div className="absolute bottom-40 right-10 w-28 h-28 border border-white/25 rounded-full animate-pulse-slow delay-3000"></div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 animate-gradient-diagonal"></div>
            </div>
            <div className="relative max-w-7xl mx-auto px-8">
              <div className={`text-center mb-16 transition-all duration-1000 ${isVisible('business-fundamentals') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-5xl font-thin mb-6">{intl.formatMessage({ id: 'dealTeaser.business_fundamentals.title' })}</h2>
                <div className="w-24 h-px bg-white mx-auto"></div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {[
                  ...(!hideListPrice ? [{
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.list_price' }),
                    value: deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'dealTeaser.business_fundamentals.tbd' }),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.location' }),
                    value: `${deal.inventory_user?.user?.city}, ${deal.inventory_user?.user?.province}`,
                    unit: ''
                  },
                  ...(deal.inventory_user?.affiliation ? [{
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.affiliation' }),
                    value: deal.inventory_user.affiliation,
                    unit: ''
                  }] : []),
                  ...(deal.inventory_user?.user?.assets_under_management ? [{
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.aum_aua' }),
                    value: formatCurrency(deal.inventory_user.user.assets_under_management),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.number_of_clients' }),
                    value: deal.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'dealTeaser.business_fundamentals.confidential' }),
                    unit: ''
                  },
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.year_established' }),
                    value: deal.inventory_user?.user?.years_practicing ?
                      (new Date().getFullYear() - deal.inventory_user.user.years_practicing).toString() :
                      getData('year_established') || intl.formatMessage({ id: 'dealTeaser.business_fundamentals.historic' }),
                    unit: ''
                  },
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.commission_percentage' }),
                    value: deal.inventory_user?.user?.percent_commission ? Math.round(deal.inventory_user?.user?.percent_commission * 100).toString() : '-',
                    unit: '%'
                  },
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.fee_percentage' }),
                    value: deal.inventory_user?.user?.percent_fee_based ? Math.round(deal.inventory_user?.user?.percent_fee_based * 100).toString() : '-',
                    unit: '%'
                  },
                  ...(deal.inventory_user?.user?.team_size && deal.inventory_user.user.team_size > 0 ? [{
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.team_size' }),
                    value: deal.inventory_user.user.team_size.toString(),
                    unit: ''
                  }] : []),
                  ...(deal.inventory_user?.user?.number_of_employees && deal.inventory_user.user.number_of_employees > 0 ? [{
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.number_of_employees' }),
                    value: deal.inventory_user.user.number_of_employees.toString(),
                    unit: ''
                  }] : []),
                  {
                    metric: intl.formatMessage({ id: 'dealTeaser.business_fundamentals.business_continuity_plan' }),
                    value: getData('has_business_continuity_plan') ? intl.formatMessage({ id: 'dealTeaser.business_fundamentals.yes' }) : intl.formatMessage({ id: 'dealTeaser.business_fundamentals.no' }),
                    unit: ''
                  }
                  
                ].map((item, index) => (
                  <div key={index} className={`text-center transition-all duration-1000 ${isVisible('business-fundamentals') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${300 + index * 100}ms`}}>
                    <div className="text-3xl font-thin mb-4 hover:scale-110 transition-transform duration-300">{item.value}<span className="text-lg">{item.unit}</span></div>
                    <div className="text-sm font-light tracking-wide opacity-80 uppercase">{item.metric}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Financial Performance */}
          <div className="bg-succession-bg-primary py-24 page-break-before" data-scroll-id="financial-performance">
            <div className="max-w-7xl mx-auto px-8">
              <div className={`mb-16 transition-all duration-1000 ${isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-5xl font-thin mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.financial_performance.title' })}</h2>
                <div className="w-24 h-px bg-succession-text-primary"></div>
              </div>
              
              {/* Trailing 12-Month Revenue Breakdown */}
              <div className="mb-16">
                <h3 className="text-3xl font-thin mb-8 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.financial_performance.trailing_12_month_revenue' })}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {[
                    {
                      label: intl.formatMessage({ id: 'dealTeaser.financial_performance.t12_revenue' }),
                      amount: formatCurrency(deal.inventory_user?.user?.gross_production),
                      description: intl.formatMessage({ id: 'dealTeaser.financial_performance.total_annual_revenue' }),
                      highlight: true
                    },
                    {
                      label: intl.formatMessage({ id: 'dealTeaser.financial_performance.t12_recurring_revenue' }),
                      amount: formatCurrency(deal.inventory_user?.user?.recurring_revenue),
                      description: intl.formatMessage({ id: 'dealTeaser.financial_performance.recurring_revenue_stream' })
                    },
                    {
                      label: intl.formatMessage({ id: 'dealTeaser.financial_performance.fee_based_revenue' }),
                      amount: formatCurrency(deal.inventory_user?.user?.fee_based_revenue),
                      description: intl.formatMessage({ id: 'dealTeaser.financial_performance.advisory_fees' })
                    },
                    {
                      label: intl.formatMessage({ id: 'dealTeaser.financial_performance.trailer_revenue' }),
                      amount: formatCurrency(deal.inventory_user?.user?.trailer_revenue),
                      description: intl.formatMessage({ id: 'dealTeaser.financial_performance.trailing_commissions' })
                    },
                    {
                      label: intl.formatMessage({ id: 'dealTeaser.financial_performance.transactional_revenue' }),
                      amount: formatCurrency(deal.inventory_user?.user?.transactional_revenue),
                      description: intl.formatMessage({ id: 'dealTeaser.financial_performance.transaction_based' })
                    }
                  ].map((item, index) => (
                    <div key={index} className={`${item.highlight ? 'border-2 border-succession-primary bg-succession-primary-light' : 'border border-succession-border bg-succession-bg-primary'} p-6 rounded-lg transition-all duration-1000 hover:shadow-xl hover:-translate-y-2 ${isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${200 + index * 100}ms`}}>
                      <div className="text-2xl font-bold text-succession-text-primary mb-2">{item.amount}</div>
                      <div className="text-sm font-semibold text-succession-text-primary mb-1">{item.label}</div>
                      <div className="text-xs text-succession-text-secondary">{item.description}</div>
                    </div>
                  ))}
                </div>
              </div>




          
              {/* Gross Production Chart - Only show if there's historical data */}
              {(deal.inventory_user?.user?.year_1_gross > 0 || 
                deal.inventory_user?.user?.year_2_gross > 0 || 
                deal.inventory_user?.user?.year_3_gross > 0 || 
                deal.inventory_user?.user?.year_4_gross > 0) && (
              <div className={`bg-succession-bg-secondary p-12 transition-all duration-1000 ${isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '600ms'}}>
                <h3 className="text-3xl font-thin mb-8 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.financial_performance.gross_production_history' })}</h3>
                <div className="flex items-end justify-between h-64 border-b border-succession-border">
                  {[
                    { year: new Date().getFullYear() - 3, value: deal.inventory_user?.user?.year_4_gross || 0, label: '2021' },
                    { year: new Date().getFullYear() - 2, value: deal.inventory_user?.user?.year_3_gross || 0, label: '2022' },
                    { year: new Date().getFullYear() - 1, value: deal.inventory_user?.user?.year_2_gross || 0, label: '2023' },
                    { year: new Date().getFullYear(), value: deal.inventory_user?.user?.year_1_gross || 0, label: '2024' }
                  ].map((item, index) => {
                    const maxValue = Math.max(
                      deal.inventory_user?.user?.year_1_gross || 0,
                      deal.inventory_user?.user?.year_2_gross || 0,
                      deal.inventory_user?.user?.year_3_gross || 0,
                      deal.inventory_user?.user?.year_4_gross || 0
                    );
                    const height = maxValue > 0 ? (item.value / maxValue) * 200 : 20;
                    
                    return (
                      <div key={index} className="flex flex-col items-center flex-1 group">
                        <div 
                          className="w-16 bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-1000 ease-out relative overflow-hidden group-hover:from-blue-700 group-hover:to-blue-500 rounded-t-md flex items-center justify-center"
                          style={{ height: `${height}px` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-transparent animate-bar-shimmer"></div>
                          <div className="text-xs font-semibold text-white z-10 transform -rotate-90 whitespace-nowrap">
                            {formatCurrency(item.value)}
                          </div>
                        </div>
                        <div className="mt-4 text-sm font-medium text-succession-text-primary">{item.year}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-8 text-center">
                  <p className="text-sm text-succession-text-secondary">
                    {intl.formatMessage({ id: 'dealTeaser.financial_performance.gross_production_trend' })}
                  </p>
                </div>
              </div>
              )}
              
              {/* Assets Information */}
              {(deal?.assets_included || deal?.intangible_assets_included) && (
                <div className={`bg-succession-bg-primary border border-succession-border p-8 transition-all duration-1000 ${isVisible('financial-performance') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '800ms'}}>
                  <h3 className="text-3xl font-thin mb-8 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.financial_performance.assets_information' })}</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    {deal?.assets_included && (
                      <div>
                        <h4 className="text-lg font-medium text-succession-text-primary mb-4">{intl.formatMessage({ id: 'dealTeaser.financial_performance.assets_included_in_purchase' })}</h4>
                        <p className="text-succession-text-secondary leading-relaxed">{deal.assets_included}</p>
                      </div>
                    )}
                    {deal?.intangible_assets_included && (
                      <div>
                        <h4 className="text-lg font-medium text-succession-text-primary mb-4">{intl.formatMessage({ id: 'dealTeaser.financial_performance.intangible_assets' })}</h4>
                        <p className="text-succession-text-secondary leading-relaxed">{deal.intangible_assets_included}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Specializations */}
          <div className="bg-succession-bg-primary py-24" data-scroll-id="products-services">
            <div className="max-w-7xl mx-auto px-8">
              <div className={`mb-16 transition-all duration-1000 ${isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-5xl font-thin mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.specializations.title' })}</h2>
                <div className="w-24 h-px bg-succession-text-primary"></div>
              </div>
              
              {/* Practice Information */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {[
                  // Only include Practice Areas if tag_list exists and has items
                  ...(deal.inventory_user?.user?.tag_list ? [{
                    period: intl.formatMessage({ id: 'dealTeaser.specializations.practice_areas' }),
                    amount: split(deal.inventory_user.user.tag_list, ',')
                      .slice(0, 3)
                      .map(tag =>
                        tag.trim()
                           .replace(/_/g, ' ')
                           .replace(/\b\w/g, l => l.toUpperCase())
                      )
                      .join(', '),
                    label: intl.formatMessage({ id: 'dealTeaser.specializations.service_specializations' })
                  }] : []),

                  // Only include Primary Markets if city or province exists
                  ...((deal.inventory_user?.user?.city || deal.inventory_user?.user?.province) ? [{
                    period: intl.formatMessage({ id: 'dealTeaser.specializations.primary_markets' }),
                    amount: `${deal.inventory_user?.user?.city || ''}${deal.inventory_user?.user?.city && deal.inventory_user?.user?.province ? ', ' : ''}${deal.inventory_user?.user?.province || ''}`,
                    label: intl.formatMessage({ id: 'dealTeaser.specializations.geographic_focus' })
                  }] : []),

                  // Only include Specialized Business if niche_market or specialization exists
                  ...((deal.inventory_user?.user?.niche_market || deal.inventory_user?.user?.specialization) ? [{
                    period: intl.formatMessage({ id: 'dealTeaser.specializations.specialized_business' }),
                    amount: deal.inventory_user?.user?.niche_market || deal.inventory_user?.user?.specialization,
                    label: intl.formatMessage({ id: 'dealTeaser.specializations.business_focus' })
                  }] : []),

                  // Only include Investment Models if discretionary_models or investment_philosophy exists
                  ...((deal.inventory_user?.user?.discretionary_models || deal.inventory_user?.user?.investment_philosophy) ? [{
                    period: intl.formatMessage({ id: 'dealTeaser.specializations.investment_models' }),
                    amount: deal.inventory_user?.user?.discretionary_models ? intl.formatMessage({ id: 'dealTeaser.business_fundamentals.yes' }) : deal.inventory_user?.user?.investment_philosophy,
                    label: intl.formatMessage({ id: 'dealTeaser.specializations.discretionary_investment_models' })
                  }] : []),

                  // Only include What's Your Why if motivation or why_selling exists
                  ...((deal.inventory_user?.user?.motivation || deal.inventory_user?.user?.why_selling) ? [{
                    period: intl.formatMessage({ id: 'dealTeaser.specializations.whats_your_why' }),
                    amount: deal.inventory_user?.user?.motivation || deal.inventory_user?.user?.why_selling,
                    label: intl.formatMessage({ id: 'dealTeaser.specializations.personal_motivation' })
                  }] : [])
                ].map((item, index) => (
                  <div key={index} className={`border border-succession-border p-8 transition-all duration-1000 hover:shadow-xl hover:-translate-y-2 hover:border-succession-primary ${isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: `${200 + index * 100}ms`}}>
                    <div className="text-sm font-medium text-succession-text-secondary uppercase tracking-wide mb-2">{item.period}</div>
                    <div className="text-lg font-medium text-succession-text-primary mb-2 leading-relaxed">{item.amount}</div>
                    <div className="text-sm text-succession-text-secondary font-light">{item.label}</div>
                  </div>
                ))}
              </div>

              {/* Products and Services Additional Comments */}
              {deal?.products_and_services_comments && (
                <div className={`bg-succession-bg-secondary p-8 rounded-lg transition-all duration-1000 ${isVisible('products-services') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '600ms'}}>
                  <h3 className="text-2xl font-thin mb-4 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.specializations.products_and_services' })}</h3>
                  <p className="text-lg text-succession-text-secondary leading-relaxed">{deal.products_and_services_comments}</p>
                </div>
              )}
            </div>
          </div>

          {/* Client Demographics */}
          <div className="bg-succession-bg-secondary py-24" data-scroll-id="client-demographics">
            <div className="max-w-6xl mx-auto px-8">
              <div className={`mb-16 transition-all duration-1000 ${isVisible('client-demographics') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <h2 className="text-5xl font-thin mb-6 text-succession-text-primary">
                  {companySettings.businessType === 'wealth_rj' ? intl.formatMessage({ id: 'dealTeaser.client_demographics.title_client' }) : intl.formatMessage({ id: 'dealTeaser.client_demographics.title_customer' })}
                </h2>
                <div className="w-24 h-px bg-succession-text-primary"></div>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-16 items-start">
                <div className={`transition-all duration-1000 ${isVisible('client-demographics') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                  <div className="bg-succession-bg-primary p-8 rounded-lg border border-succession-border shadow-sm">
                    <h3 className="text-2xl font-thin mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.client_demographics.client_profile' })}</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center border-b border-succession-border pb-3">
                        <span className="text-succession-text-secondary font-medium">{intl.formatMessage({ id: 'dealTeaser.client_demographics.total_clients' })}</span>
                        <span className="text-xl font-light text-succession-text-primary">{deal?.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'dealTeaser.business_fundamentals.confidential' })}</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-succession-border pb-3">
                        <span className="text-succession-text-secondary font-medium">{intl.formatMessage({ id: 'dealTeaser.client_demographics.average_client_age' })}</span>
                        <span className="text-xl font-light text-succession-text-primary">{deal?.inventory_user?.user?.avg_client_age || intl.formatMessage({ id: 'dealTeaser.client_demographics.mixed_age_groups' })}</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-succession-border pb-3">
                        <span className="text-succession-text-secondary font-medium">{intl.formatMessage({ id: 'dealTeaser.client_demographics.niche_market_focus' })}</span>
                        <span className="text-xl font-light text-succession-text-primary">{deal?.inventory_user?.user?.niche_market || intl.formatMessage({ id: 'dealTeaser.client_demographics.diversified' })}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {deal?.customer_demographics_comments && (
                  <div className={`transition-all duration-1000 ${isVisible('client-demographics') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{transitionDelay: '300ms'}}>
                    <h3 className="text-2xl font-thin mb-6 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.client_demographics.additional_comments' })}</h3>
                    <div className="bg-succession-bg-primary p-8 rounded-lg border border-succession-border shadow-sm">
                      <p className="text-lg text-succession-text-secondary leading-relaxed">{deal.customer_demographics_comments}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Investment Opportunity */}
          <div className="bg-succession-bg-secondary py-24 relative overflow-hidden" data-scroll-id="succession-opportunity">
            {/* Geometric background animation */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-16 h-16 bg-succession-primary rotate-45 animate-slow-spin"></div>
              <div className="absolute top-1/3 right-20 w-12 h-12 bg-succession-secondary animate-bounce-slow delay-500"></div>
              <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-succession-accent rounded-full animate-pulse-slow delay-1000"></div>
              <div className="absolute bottom-1/3 right-1/3 w-14 h-14 bg-succession-neutral rotate-45 animate-slow-spin delay-1500"></div>
            </div>
            <div className="relative max-w-6xl mx-auto px-8">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className={`transition-all duration-1000 ${isVisible('succession-opportunity') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                   <h2 className="text-5xl font-thin mb-8 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.succession.title' })}</h2>
                  <div className="space-y-6">
                                         <div>
                       <h3 className="text-xl font-medium text-succession-text-primary mb-3">{intl.formatMessage({ id: 'dealTeaser.succession.ideal_successor_profile' })}</h3>
                      <p className="text-lg font-light text-succession-text-secondary leading-relaxed">
                        {deal?.ideal_candidate || intl.formatMessage({ id: 'dealTeaser.succession.default_ideal_candidate' })}
                      </p>
                    </div>
                    {deal?.growth_opportunities && (
                      <div>
                        <h3 className="text-xl font-medium text-succession-text-primary mb-3">{intl.formatMessage({ id: 'dealTeaser.succession.growth_potential' })}</h3>
                        <p className="text-lg font-light text-succession-text-secondary leading-relaxed">
                          {deal.growth_opportunities}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`bg-succession-bg-primary p-12 border border-succession-border transition-all duration-1000 hover:shadow-2xl hover:border-succession-primary ${isVisible('succession-opportunity') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{transitionDelay: '300ms'}}>
                  <h3 className="text-2xl font-thin mb-8 text-succession-text-primary">{intl.formatMessage({ id: 'dealTeaser.succession.client_portfolio' })}</h3>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center border-b border-succession-border pb-4">
                      <span className="text-succession-text-secondary font-light">{intl.formatMessage({ id: 'dealTeaser.succession.total_clients' })}</span>
                      <span className="text-xl font-thin text-succession-text-primary">{deal?.inventory_user?.user?.number_of_clients || intl.formatMessage({ id: 'dealTeaser.business_fundamentals.confidential' })}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-succession-border pb-4">
                      <span className="text-succession-text-secondary font-light">{intl.formatMessage({ id: 'dealTeaser.succession.average_client_age' })}</span>
                      <span className="text-xl font-thin text-succession-text-primary">{deal?.inventory_user?.user?.avg_client_age || intl.formatMessage({ id: 'dealTeaser.succession.mature' })}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-succession-border pb-4">
                      <span className="text-succession-text-secondary font-light">{intl.formatMessage({ id: 'dealTeaser.succession.niche_focus' })}</span>
                      <span className="text-xl font-thin text-succession-text-primary">{deal?.inventory_user?.user?.niche_market || intl.formatMessage({ id: 'dealTeaser.succession.diversified' })}</span>
                    </div>
                  </div>

                  {deal?.inventory_user?.user?.tag_list && (
                    <div className="mt-8">
                      <h4 className="text-lg font-medium text-succession-text-primary mb-4">{intl.formatMessage({ id: 'dealTeaser.succession.service_portfolio' })}</h4>
                      <div className="flex flex-wrap gap-2">
                        {split(get(deal, 'inventory_user.user.tag_list', []), ',').map((tag, index) => (
                          <span key={index} className={`px-3 py-1 bg-succession-primary text-white text-sm font-light tracking-wide opacity-0 animate-fade-in-scale delay-${1000 + index * 150} hover:bg-succession-primary-hover hover:scale-110 transition-all duration-300 cursor-default`}>
                            {tag.trim().replace(/\b\w/g, l => l.toUpperCase()).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            </div>
          </div>
        </div>

                     {/* Succession Terms */}
           <div className="bg-gray-900 text-white py-24" data-scroll-id="succession-terms">
             <div className="max-w-4xl mx-auto px-8 text-center">
               <h2 className={`text-5xl font-thin mb-12 transition-all duration-1000 ${isVisible('succession-terms') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{intl.formatMessage({ id: 'dealTeaser.succession_terms.title' })}</h2>
              <div className={`grid ${hideListPrice ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-8 mb-12`}>
                {!hideListPrice && (
                  <div className={`transition-all duration-1000 hover:scale-110 ${isVisible('succession-terms') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '200ms'}}>
                    <div className="text-4xl font-thin mb-4">{deal.list_price ? formatCurrency(deal.list_price) : intl.formatMessage({ id: 'dealTeaser.succession_terms.poa' })}</div>
                    <div className="text-sm font-light tracking-wide opacity-80 uppercase">{intl.formatMessage({ id: 'dealTeaser.succession_terms.listed_price' })}</div>
                  </div>
                )}

                <div className={`transition-all duration-1000 hover:scale-110 ${isVisible('succession-terms') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{transitionDelay: '500ms'}}>
                  <div className="text-4xl font-thin mb-4">{getData('has_business_continuity_plan') ? intl.formatMessage({ id: 'dealTeaser.business_fundamentals.yes' }).toUpperCase() : 'CUSTOM'}</div>
                  <div className="text-sm font-light tracking-wide opacity-80 uppercase">{intl.formatMessage({ id: 'dealTeaser.succession_terms.continuity_plan' })}</div>
                </div>
              </div>
                             <div className="border-t border-gray-700 pt-8">
                 <p className="text-lg font-light opacity-90">
                   {intl.formatMessage({ id: 'dealTeaser.succession_terms.confidential_disclaimer' })}
                 </p>
               </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes fade-in-up {
            from { 
              opacity: 0; 
              transform: translateY(30px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
          
          @keyframes fade-in-left {
            from { 
              opacity: 0; 
              transform: translateX(-30px); 
            }
            to { 
              opacity: 1; 
              transform: translateX(0); 
            }
          }
          
          @keyframes fade-in-right {
            from { 
              opacity: 0; 
              transform: translateX(30px); 
            }
            to { 
              opacity: 1; 
              transform: translateX(0); 
            }
          }
          
          @keyframes fade-in-scale {
            from { 
              opacity: 0; 
              transform: scale(0.8); 
            }
            to { 
              opacity: 1; 
              transform: scale(1); 
            }
          }
          
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) rotate(0deg); 
            }
            33% { 
              transform: translateY(-10px) rotate(120deg); 
            }
            66% { 
              transform: translateY(5px) rotate(240deg); 
            }
          }
          
          @keyframes gradient-shift {
            0%, 100% { 
              background-position: 0% 50%; 
            }
            50% { 
              background-position: 100% 50%; 
            }
          }
          
          @keyframes gradient-diagonal {
            0%, 100% { 
              background-position: 0% 0%; 
            }
            50% { 
              background-position: 100% 100%; 
            }
          }
          
          @keyframes shimmer {
            0% { 
              transform: translateX(-100%); 
            }
            100% { 
              transform: translateX(100%); 
            }
          }
          
          @keyframes bar-shimmer {
            0%, 100% { 
              transform: translateY(100%); 
              opacity: 0; 
            }
            50% { 
              transform: translateY(0%); 
              opacity: 1; 
            }
          }
          
          @keyframes pulse-slow {
            0%, 100% { 
              opacity: 0.1; 
              transform: scale(1); 
            }
            50% { 
              opacity: 1; 
              transform: scale(1.05); 
            }
          }
          
          @keyframes bounce-slow {
            0%, 100% { 
              transform: translateY(0px); 
            }
            50% { 
              transform: translateY(-10px); 
            }
          }
          
          @keyframes slow-spin {
            from { 
              transform: rotate(0deg); 
            }
            to { 
              transform: rotate(360deg); 
            }
          }
          
          @keyframes number-reveal {
            0% { 
              opacity: 0; 
              transform: translateY(20px) scale(0.8); 
            }
            100% { 
              opacity: 1; 
              transform: translateY(0px) scale(1); 
            }
          }
          
          @keyframes typewriter {
            0% { 
              width: 0; 
            }
            100% { 
              width: 100%; 
            }
          }
          
          @keyframes blink {
            0%, 50% { 
              opacity: 1; 
            }
            51%, 100% { 
              opacity: 0; 
            }
          }
          
          .animate-fade-in {
            animation: fade-in 1s ease-out forwards;
          }
          
          .animate-fade-in-up {
            animation: fade-in-up 1s ease-out forwards;
          }
          
          .animate-fade-in-left {
            animation: fade-in-left 1s ease-out forwards;
          }
          
          .animate-fade-in-right {
            animation: fade-in-right 1s ease-out forwards;
          }
          
          .animate-fade-in-scale {
            animation: fade-in-scale 0.8s ease-out forwards;
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          .animate-gradient-shift {
            background-size: 200% 200%;
            animation: gradient-shift 8s ease infinite;
          }
          
          .animate-gradient-diagonal {
            background-size: 200% 200%;
            animation: gradient-diagonal 12s ease infinite;
          }
          
          .animate-shimmer {
            animation: shimmer 2s ease-in-out infinite;
          }
          
          .animate-bar-shimmer {
            animation: bar-shimmer 3s ease-in-out infinite;
          }
          
          .animate-pulse-slow {
            animation: pulse-slow 4s ease-in-out infinite;
          }
          
          .animate-bounce-slow {
            animation: bounce-slow 3s ease-in-out infinite;
          }
          
          .animate-slow-spin {
            animation: slow-spin 20s linear infinite;
          }
          
          .animate-number-reveal {
            animation: number-reveal 1.5s ease-out forwards;
          }
          
          .animate-typewriter {
            overflow: hidden;
            border-right: 2px solid;
            white-space: nowrap;
            animation: typewriter 3s steps(40) forwards, blink 0.5s step-end infinite alternate;
          }
          
          .animation-delay-200 { animation-delay: 200ms; }
          .animation-delay-300 { animation-delay: 300ms; }
          .animation-delay-400 { animation-delay: 400ms; }
          .animation-delay-500 { animation-delay: 500ms; }
          .animation-delay-700 { animation-delay: 700ms; }
          .animation-delay-1000 { animation-delay: 1000ms; }
          .animation-delay-1200 { animation-delay: 1200ms; }
          .animation-delay-1500 { animation-delay: 1500ms; }
          .animation-delay-2000 { animation-delay: 2000ms; }
          .animation-delay-2500 { animation-delay: 2500ms; }
          .animation-delay-3000 { animation-delay: 3000ms; }
          .animation-delay-3500 { animation-delay: 3500ms; }
          
          .delay-0 { animation-delay: 0ms; }
          .delay-500 { animation-delay: 500ms; }
          .delay-1000 { animation-delay: 1000ms; }
          .delay-1500 { animation-delay: 1500ms; }
          .delay-2000 { animation-delay: 2000ms; }
          .delay-2500 { animation-delay: 2500ms; }
          .delay-3000 { animation-delay: 3000ms; }
          .delay-3500 { animation-delay: 3500ms; }
          
          /* Hover effects */
          .hover-lift:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          }
          
          .hover-glow:hover {
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
          }
          
          .hover-gradient:hover {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          
          /* Scroll-triggered animations */
          @media (prefers-reduced-motion: no-preference) {
            .scroll-reveal {
              opacity: 0;
              transform: translateY(50px);
              transition: all 0.8s ease-out;
            }
            
            .scroll-reveal.revealed {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* PDF-specific styles */
          @media print {
            .pdf-hide {
              display: none !important;
            }
            
            .page-break-before {
              page-break-before: always;
            }
            
            .page-break-after {
              page-break-after: always;
            }
            
            body {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            * {
              animation: none !important;
              transition: none !important;
            }
          }
        `}</style>
        
        {/* Teaser description */}
        <Card className="p-8 mt-12 mb-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-succession-primary to-succession-secondary flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-succession-text-primary mb-2">{intl.formatMessage({ id: 'dealTeaser.about_teaser.title' })}</h3>
              <p className="text-sm text-succession-text-secondary leading-relaxed">
                {intl.formatMessage({ id: 'dealTeaser.about_teaser.description' })}
              </p>
            </div>
          </div>
        </Card>
        
        {/* Action Buttons */}
        <div className="mb-8">
          {renderActionButtons()}
        </div>
        
        {/* Financing Options - Show for buyers */}
        {!isDealOwner && renderLenderOffers()}
        {!isDealOwner && renderLenderInquiry()}
        
        {/* Conditional Components */}
        {renderExpressionsOfInterest()}
        {renderCashFlowAnalysis()}
        {renderDocuments()}
      </div>

      {/* Match Preferences Modal - for deal owners to edit match preferences */}
      <MatchPreferencesModal
        isOpen={showMatchPreferencesModal}
        onClose={() => setShowMatchPreferencesModal(false)}
        currentUser={currentUser}
        company={companySettings}
      />

      <GuidedTour
        isOpen={tourOpen}
        steps={tourSteps}
        onClose={handleTourClose}
        onComplete={handleTourClose}
        onReplayRequest={() => setTourOpen(true)}
      />
    </>
  );
};

DealTeaser.propTypes = {
  deal: PropTypes.object.isRequired,
  refetchDeal: PropTypes.func.isRequired,
  isDealOwner: PropTypes.bool,
  dealInterestId: PropTypes.string,
  currentUser: PropTypes.object,
  userMotivation: PropTypes.string
};

// Wrap the export with error boundary
export default function DealTeaserWithErrorBoundary(props) {
  // Enhanced validation for the deal object and its required properties
  if (!props.deal || !props.deal.deal_name) {
    return (
      <div className="p-8 bg-white rounded-md shadow">
        <h2 className="text-xl font-bold mb-4">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.deal_not_available" />
        </h2>
        <p>
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.unable_to_load" />
        </p>
        <Link to="/succession/deals" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.return_to_deals" />
        </Link>
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={
      <div className="p-8">
        <h2 className="text-xl font-bold mb-4">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.loading_error" />
        </h2>
        <p>
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.refresh_message" />
        </p>
        <Link to="/succession/deals" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover">
          <FormattedMessage id="succession_deals.deal_teaser.error_boundary.return_to_deals" />
        </Link>
      </div>
    }>
      <DealTeaser {...props} />
    </ErrorBoundary>
  );
}